import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import {
  Box,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";

import { type ChecklistStepKey, getOnboardingChecklist } from "@/api/onboarding";
import { usePermission } from "@/auth/usePermission";

const ROUTES: Record<ChecklistStepKey, string> = {
  tenant_branded: "/admin/tenant-settings",
  organization_created: "/organizations",
  slot_template_created: "/settings",
  practitioner_added: "/practitioners",
  membership_active: "/practitioners",
  users_invited: "/admin/users",
  shift_pattern_created: "/admin/shift-patterns",
  planning_published: "/planning",
};

export function OnboardingCard() {
  const { t } = useTranslation();
  const canSeeAdminWorkflow = usePermission("tenant:update");

  const checklist = useQuery({
    queryKey: ["onboarding-checklist"],
    queryFn: getOnboardingChecklist,
    enabled: canSeeAdminWorkflow,
    staleTime: 60_000,
  });

  if (!canSeeAdminWorkflow || !checklist.data) return null;
  // Hide when everything's done
  if (checklist.data.completed === checklist.data.total) return null;

  const pct = Math.round(
    (checklist.data.completed / checklist.data.total) * 100,
  );

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ sm: "center" }}
          spacing={1}
          sx={{ mb: 1.5 }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {t("onboarding.title")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("onboarding.subtitle")}
            </Typography>
          </Box>
          <Chip
            label={t("onboarding.progress", {
              completed: checklist.data.completed,
              total: checklist.data.total,
            })}
            color={pct === 100 ? "success" : "primary"}
          />
        </Stack>
        <LinearProgress
          variant="determinate"
          value={pct}
          sx={{ mb: 2, height: 8, borderRadius: 4 }}
        />
        <Stack spacing={1}>
          {checklist.data.steps.map((s) => {
            const route = ROUTES[s.key];
            return (
              <Stack
                key={s.key}
                direction="row"
                spacing={1.5}
                alignItems="center"
                component={RouterLink}
                to={route}
                sx={{
                  textDecoration: "none",
                  color: "inherit",
                  p: 1,
                  borderRadius: 1,
                  "&:hover": { bgcolor: "action.hover" },
                  opacity: s.done ? 0.6 : 1,
                }}
              >
                {s.done ? (
                  <CheckCircleIcon color="success" fontSize="small" />
                ) : (
                  <RadioButtonUncheckedIcon color="action" fontSize="small" />
                )}
                <Typography
                  variant="body2"
                  sx={{
                    flex: 1,
                    textDecoration: s.done ? "line-through" : "none",
                  }}
                >
                  {t(`onboarding.step.${s.key}`)}
                </Typography>
                {s.count > 0 && (
                  <Chip size="small" variant="outlined" label={s.count} />
                )}
              </Stack>
            );
          })}
        </Stack>
      </CardContent>
    </Card>
  );
}
